import type { EditorBody } from "@/components/document-editor"

export type Priority = "low" | "normal" | "high" | "urgent"
export type ColumnCategory = "todo" | "in_progress" | "done"

export interface Person {
  id: number
  name: string
}

export interface TicketCard {
  key: string
  title: string
  priority: Priority
  due_on: string | null
  estimate: number | null
  assignee: Person | null
  completed: boolean
}

export interface Column {
  id: number
  name: string
  category: ColumnCategory
  position: number
  wip_limit: number | null
  over_wip_limit: boolean
  tickets: TicketCard[]
}

export interface BoardShare {
  id: number
  user_id: number
  name: string
  email: string
  access: "viewer" | "editor"
}

export interface BoardRow {
  key: string
  name: string
  description: string | null
  visibility: "private" | "company"
  project: { id: number; name: string } | null
  ticket_count: number
  archived: boolean
  can_edit: boolean
}

export interface BoardDetail {
  key: string
  name: string
  description: string | null
  visibility: "private" | "company"
  default_access: "view" | "edit"
  project: { id: number; name: string } | null
  archived: boolean
  can_edit: boolean
  can_administer: boolean
  shares: BoardShare[]
}

export interface TicketDetail {
  key: string
  title: string
  body: EditorBody
  priority: Priority
  estimate: number | null
  due_on: string | null
  board_column_id: number
  assignee: Person | null
  reporter: Person | null
  completed: boolean
  archived: boolean
  can_edit: boolean
  can_destroy: boolean
}

export const PRIORITY_LABELS: Record<Priority, string> = {
  low: "Low",
  normal: "Normal",
  high: "High",
  urgent: "Urgent",
}

export const PRIORITY_BADGE: Record<Priority, "default" | "secondary" | "destructive" | "outline"> = {
  low: "outline",
  normal: "secondary",
  high: "default",
  urgent: "destructive",
}

export interface Navigation {
  boards: { key: string; name: string }[]
  active_board_key: string | null
  can_create: boolean
  projects: { id: number; name: string }[]
}

export interface Comment {
  id: number
  body: EditorBody
  author: Person | null
  created_at: string
  edited: boolean
  can_edit: boolean
  can_destroy: boolean
}

export interface Activity {
  id: number
  action: "created" | "moved" | "assigned" | "unassigned" | "priority" | "due_on" | "estimate"
  from: string | null
  to: string | null
  actor: Person | null
  created_at: string
}

export type FieldType = "text" | "number" | "date" | "checkbox" | "select" | "multi_select" | "person"

export interface BoardFieldDefinition {
  id: number
  name: string
  field_type: FieldType
  options: string[]
  position: number
  archived: boolean
}

export const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  text: "Text",
  number: "Number",
  date: "Date",
  checkbox: "Checkbox",
  select: "Choice",
  multi_select: "Multiple choice",
  person: "Person",
}

export function choiceType(type: FieldType) {
  return type === "select" || type === "multi_select"
}
